import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { get, getProperties } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import dateUtils from 'donor-elf-web/lib/date-utils';
import objectUtils from 'donor-elf-web/lib/object-utils';
import type FormatDateService from 'donor-elf-web/services/format-date-service';
import type FundsService from 'donor-elf-web/services/funds-service';
import type Settings from 'donor-elf-web/services/settings';
import validateDateOrEmpty from 'donor-elf-web/validators/date-or-empty';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import FileSaver from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';

const filterValidation = {
  from: validateDateOrEmpty(),
  to: validateDateOrEmpty(),
};

export default class MonthlySummaryController extends Controller {
  @service declare formatDateService: FormatDateService;
  @service declare fundsService: FundsService;
  @service declare router: RouterService;
  @service declare settings: Settings;
  @service declare store: Store;

  queryParams = ['from', 'to'];

  @tracked from: string = '';
  @tracked to: string = '';
  @tracked months: any[] = [];
  @tracked total: any = { income: 0, expense: 0 };
  @tracked filterExpanded = false;
  @tracked filterChangeset: any = null;
  @tracked sortMonthsBy = ['from:desc'];
  @tracked loading = false;

  get sortedMonths() {
    return sortByProperties(this.months, this.sortMonthsBy);
  }

  get includeExpenses() {
    let include = true;
    if (this.settings.orgSettings?.incomeOnly) {
      include = this.months.filter((x) => x.expense < 0).length > 0;
    }
    return include;
  }

  get includeBalance() {
    return (
      !this.settings.orgSettings?.incomeOnly &&
      !this.settings.orgSettings?.hideMonthlyBalance
    );
  }

  handleLifecycle = () => {
    this.fetchData();
  };

  fetchData() {
    this.loading = true;

    let filter = {
      from: this.from,
      to: this.to,
      includeBalance: true,
    };

    if (isEmpty(filter.from)) {
      filter.from = moment().subtract(11, 'months').format('YYYY-MM-DD');
    }
    if (isEmpty(filter.to)) {
      filter.to = moment().format('YYYY-MM-DD');
    }

    filter.from = moment(filter.from).startOf('month').format('YYYY-MM-DD');
    filter.to = moment(filter.to).endOf('month').format('YYYY-MM-DD');

    let filterRecord = {
      from: dateUtils.deserializeDateOnly(filter.from),
      to: dateUtils.deserializeDateOnly(filter.to),
    };

    let filterChangeset = Changeset(
      filterRecord,
      lookupValidator(filterValidation),
      filterValidation,
    );

    this.filterChangeset = filterChangeset;

    return this.fundsService.confirmValidFundToken().then(() => {
      return this.store
        .query('monthly-summary', {
          filter: objectUtils.decamelizeKeys(filter),
        })
        .then((records) => {
          const byMonth: Record<string, number> = {};
          const balanceByMonth: Record<string, number> = {};

          let total = { income: 0, expense: 0 };

          records.forEach((record) => {
            const { year, month, amount, kind } = record;

            if (kind === 'balance') {
              balanceByMonth[`${year}-${month}`] = amount;
            } else {
              byMonth[`${year}-${month}-${kind}`] = amount;
            }
          });

          let months = [];
          let date = moment(filter.from);
          let to = moment(filter.to);

          while (date < to) {
            const dateKey = date.format('YYYY-M');
            const income = get(byMonth, `${dateKey}-income`) || 0;
            const expense = get(byMonth, `${dateKey}-expense`) || 0;
            const balance = get(balanceByMonth, dateKey) || 0;
            const diff = income + expense;

            months.push({
              label: date.format('MMM YYYY'),
              from: date.startOf('month').format('YYYY-MM-DD'),
              to: date.endOf('month').format('YYYY-MM-DD'),
              income: income,
              expense: expense,
              balance: balance,
              diff: diff,
            });

            total.income += income;
            total.expense += expense;

            date.add(1, 'month').startOf('month');
          }

          this.months = months;
          this.total = total;
          this.loading = false;
        });
    });
  }

  get filterMessage() {
    const messages = [];
    const { formatDateService, filterChangeset } = getProperties(
      this,
      'formatDateService',
      'filterChangeset',
    );

    if (!filterChangeset) {
      return '';
    }

    const { from, to } = getProperties(filterChangeset, 'from', 'to');

    messages.push(
      `Between ${formatDateService.format(from)} and ${formatDateService.format(to)}`,
    );

    return messages.join(', ');
  }

  get actionsMenuItems() {
    return [
      {
        label: this.filterExpanded ? 'Collapse Filter' : 'Adjust Filter',
        svgJar: 'filter',
        onClick: () => {
          this.toggleExpanded();
        },
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: () => {
          this.download();
        },
      },
    ];
  }

  applyFilter = () => {
    const filterChangeset = this.filterChangeset;

    if (this.filterChangeset === 'isPristine') {
      alert('No filter values have changed');
      return;
    }

    this.from = dateUtils.serializeToDateOnly(
      this.filterChangeset.get('from'),
      {
        invalidValue: '',
      },
    );

    this.to = dateUtils.serializeToDateOnly(this.filterChangeset.get('to'), {
      invalidValue: '',
    });

    this.fetchData();
  };

  toggleExpanded = () => {
    this.toggleProperty('filterExpanded');
  };

  toggleSortDirection = () => {
    if (this.sortMonthsBy[0] === 'from:desc') {
      this.sortMonthsBy = ['from:asc'];
    } else {
      this.sortMonthsBy = ['from:desc'];
    }
  };

  handleTransitionTo(name: string, options: any) {
    this.router.transitionTo(name, options);
  }

  download = () => {
    let rows = [];
    let { includeExpenses, includeBalance } = getProperties(
      this,
      'includeExpenses',
      'includeBalance',
    );
    let headerRow = ['Month', 'Income'];
    if (includeExpenses) {
      headerRow.push('Expense');
    }
    if (includeBalance) {
      headerRow.push('Balance');
    }
    rows.push(headerRow);
    this.sortedMonths.forEach((item) => {
      let row = [item.label, item.income];
      if (includeExpenses) {
        row.push(item.expense);
      }
      if (includeBalance) {
        row.push(item.balance);
      }
      rows.push(row);
    });
    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'monthly-summary.csv', true);
  };
}
